import { createContext, useContext, useEffect, useMemo, useState,
         type ReactElement, type ReactNode } from 'react';
import { demanderQuiJeSuis, seConnecter, seDeconnecter,
         type EtatSession, type VueSession } from '../session.js';

/**
 * LA SESSION, DISPONIBLE PARTOUT SANS ETRE TRANSMISE PARTOUT.
 *
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║ UNE SEULE INTERROGATION DE `/auth/moi` PAR CHARGEMENT.                ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 *
 * Sans contexte, chaque ecran ayant besoin du role rappellerait `/auth/moi` --
 * un appel par navigation, et des vues qui divergent si la session expire
 * entre deux.
 *
 * ── LE CONTEXTE PORTE L'ETAT, PAS LES REGLES ────────────────────────────
 *
 * Il dit QUI est connecte et avec quel role sur quel espace. Il ne decide
 * jamais ce que ce role permet : l'API tranche, et l'interface se contente de
 * ne pas proposer ce qu'elle sait ferme.
 */

interface ValeurSession {
  readonly etat: EtatSession;
  readonly connecter: (email: string, motDePasse: string) => Promise<void>;
  readonly deconnecter: () => Promise<void>;
  /** Relit `/auth/moi` : apres un changement de role, ou sur 401 inattendu. */
  readonly rafraichir: () => Promise<void>;
}

/**
 * 28-D — REPARTITION MESUREE, RIEN A DEPLACER.
 *
 * `session.ts` TRANSPORTE et TRADUIT : il appelle les routes, pose le jeton
 * CSRF par un seul chemin, et convertit chaque echec en une phase de
 * `EtatSession` -- 401 en `anonyme`, tout le reste en `indisponible` (28-AU).
 * Ce fichier HEBERGE et EXPOSE : il tient l etat, enveloppe les trois
 * fonctions, et ne prend AUCUNE decision metier. Aucun message n est
 * reformule ici, aucun code de statut n est relu.
 *
 * ── `rafraichir` N A AUCUN APPELANT, ET C EST DELIBERE ──────────────────
 *
 * Le mecanisme existe pour le « 401 inattendu ». Il n est branche nulle
 * part tant que la DETTE 29 n a pas ete REPRODUITE en observant Network :
 * un 401 peut venir d une session expiree, d un jeton CSRF perdu ou d un
 * refus d autorisation. Un rafraichissement automatique les confondrait
 * toutes et MASQUERAIT la cause qu on cherche a etablir.
 * LEVEE : reproduire le scenario, identifier la cause, brancher alors le
 * rappel a l endroit qui correspond a CETTE cause.
 */
const Contexte = createContext<ValeurSession | undefined>(undefined);

export function FournisseurSession({ children }: { children: ReactNode }): ReactElement {
  const [etat, setEtat] = useState<EtatSession>({ phase: 'chargement' });

  useEffect(() => {
    // AU CHARGEMENT, UNE SEULE QUESTION. nginx a servi les memes fichiers a
    // tout le monde ; c'est ici que l'application apprend qui la regarde.
    void demanderQuiJeSuis().then(setEtat);
  }, []);

  const valeur = useMemo<ValeurSession>(() => ({
    etat,
    connecter: async (email, motDePasse) => {
      const vue = await seConnecter(email, motDePasse);
      setEtat({ phase: 'connecte', vue });
    },
    deconnecter: async () => {
      await seDeconnecter();
      setEtat({ phase: 'anonyme' });
    },
    rafraichir: async () => { setEtat(await demanderQuiJeSuis()); },
  }), [etat]);

  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function useSession(): ValeurSession {
  const v = useContext(Contexte);
  // Une erreur de DEVELOPPEMENT, pas d'execution : un composant place hors du
  // fournisseur ne fonctionnera jamais, autant le dire tout de suite.
  if (v === undefined) throw new Error('useSession hors de FournisseurSession');
  return v;
}

/** L'espace courant, ou `undefined` si le code ne correspond a rien d'ouvert. */
export function useEspace(code: string | undefined): VueSession['locataires'][number] | undefined {
  const { etat } = useSession();
  if (etat.phase !== 'connecte' || code === undefined) return undefined;
  return etat.vue.locataires.find((l) => l.code === code);
}
